<template>
  <component
    :is="wrapperComponent"
    v-bind="wrapperProps"
    class="a-button__general-style"
    :class="dynamicClass"
    v-click="onClick"
    @blur="onBlur"
  >
    <div class="a-button__slot-wrapper">
      <slot v-if="!inProgress" />
      <a-spinner
        v-else
        class="a-button__spinner"
        :size="SPINNER_SIZE"
        :radius="SPINNER_RADIUS"
        :stroke="2"
        :color="spinnerColor"
      />
      <a-validation-message :error="error" />
    </div>
  </component>
</template>

<script>
import { propValidator, PROP_TYPES } from '@/utils/validators'
import ASpinner from 'shared/ASpinner'
import AValidationMessage from 'shared/AValidationMessage'
import { REL_ATTRIBUTE_BACKEND_ENUM } from '@fmpedia/enums'

export const BUTTON_STYLE = {
  PURPLE: 'PURPLE',
  RED: 'RED',
  BLACK: 'BLACK',
  BLACK_BORDERED: 'BLACK_BORDERED',
  WHITE: 'WHITE',
  WHITE_BORDERED: 'WHITE_BORDERED',
  GREEN: 'GREEN',
  GRAY: 'GRAY',
  GRAY_BORDERED: 'GRAY_BORDERED',
  LIGHT_GRAY_BORDERED: 'LIGHT_GRAY_BORDERED',
  NO_STYLE: 'NO_STYLE', // without styles, just button
  RING_CAPTCHA_SEND_PIN: 'RING_CAPTCHA_SEND_PIN'
}

const CLASS_BY_BUTTON_STYLE = {
  [BUTTON_STYLE.PURPLE]: 'a-button_purple',
  [BUTTON_STYLE.RED]: 'a-button_red',
  [BUTTON_STYLE.BLACK]: 'a-button_black',
  [BUTTON_STYLE.BLACK_BORDERED]: 'a-button_black--bordered',
  [BUTTON_STYLE.WHITE]: 'a-button_white',
  [BUTTON_STYLE.WHITE_BORDERED]: 'a-button_white--bordered',
  [BUTTON_STYLE.GREEN]: 'a-button_green',
  [BUTTON_STYLE.GRAY]: 'a-button_gray',
  [BUTTON_STYLE.GRAY_BORDERED]: 'a-button_gray--bordered',
  [BUTTON_STYLE.LIGHT_GRAY_BORDERED]: 'a-button_light-gray--bordered',
  [BUTTON_STYLE.RING_CAPTCHA_SEND_PIN]: 'a-button_ring-captcha-send-pin',
  [BUTTON_STYLE.NO_STYLE]: ''
}

const SPINNER_SIZE = 48
const SPINNER_RADIUS = SPINNER_SIZE / 4
export const SPINNER_COLOR = {
  WHITE: 'white',
  BLUE: 'blue'
}

const BUTTON_STYLE_VALUES = Object.values(BUTTON_STYLE)

const WRAPPER = {
  LINK: 'a-link',
  BUTTON: 'button'
}

export default {
  name: 'AButton',
  components: { ASpinner, AValidationMessage },
  props: {
    href: propValidator([PROP_TYPES.STRING, PROP_TYPES.OBJECT], false),
    type: propValidator([PROP_TYPES.STRING], false, 'button'),
    disabled: propValidator([PROP_TYPES.BOOLEAN], false),
    inProgress: propValidator([PROP_TYPES.BOOLEAN], false, false),
    directive: propValidator(
      [PROP_TYPES.STRING],
      false,
      REL_ATTRIBUTE_BACKEND_ENUM.NOFOLLOW
    ),
    trailingSlash: propValidator([PROP_TYPES.BOOLEAN], false, undefined),
    openInNewTab: propValidator([PROP_TYPES.BOOLEAN], false),
    buttonStyle: propValidator(
      [PROP_TYPES.STRING],
      false,
      BUTTON_STYLE.PURPLE,
      value => BUTTON_STYLE_VALUES.includes(value)
    ),
    spinnerColor: propValidator(
      [PROP_TYPES.STRING],
      false,
      SPINNER_COLOR.WHITE
    ),
    uppercase: propValidator([PROP_TYPES.BOOLEAN], false, false),
    error: propValidator([PROP_TYPES.OBJECT], false, () => ({}))
  },
  data() {
    return {
      SPINNER_SIZE,
      SPINNER_RADIUS,
      SPINNER_COLOR
    }
  },
  computed: {
    wrapperComponent() {
      return this.href ? WRAPPER.LINK : WRAPPER.BUTTON
    },
    linkProps() {
      return {
        to: this.href,
        directive: this.directive,
        openInNewTab: this.openInNewTab,
        disabled: this.disabled,
        trailingSlash: this.trailingSlash
      }
    },
    buttonProps() {
      return {
        disabled: this.disabled,
        type: this.type
      }
    },
    wrapperProps() {
      return this.wrapperComponent === WRAPPER.LINK
        ? this.linkProps
        : this.buttonProps
    },
    dynamicClass() {
      return {
        [CLASS_BY_BUTTON_STYLE[this.buttonStyle]]: true,
        'a-button_uppercase': this.uppercase,
        'a-button_in-progress': this.inProgress,
        'a-button_disabled': this.disabled
      }
    }
  },
  methods: {
    onClick() {
      if (this.disabled || this.inProgress) return

      this.$emit('click')
    },
    onBlur(event) {
      this.$emit('blur', event)
    }
  }
}
</script>

<style lang="scss" scoped>
.a-button__general-style {
  position: relative;
  display: block;
  width: 140px;
  height: 40px;
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;

  /deep/ * {
    font-family: inherit;
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
  }

  .a-button__slot-wrapper {
    width: 100%;
    height: 100%;
    display: inline-flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    vertical-align: top;
    padding: 0 10px;
    text-align: center;
    overflow: hidden;
  }

  .a-button__spinner {
    position: absolute;
  }

  &:hover {
    opacity: 0.7;
  }

  &.a-button_disabled {
    opacity: 0.4;
    pointer-events: none;
  }

  &.a-button_disabled:hover,
  &.a-button_white:hover,
  &.a-button_white--bordered:hover,
  &.a-button_green {
    opacity: 1;
  }

  &.a-button_uppercase * {
    text-transform: uppercase;
  }
}

.a-button_purple {
  background: $c--main;
  color: $c--white;
  border-radius: 4px;

  @include textPrimaryNavigation;
  font-weight: 700;

  &:hover {
    opacity: 1;
    background: $c--dark-purple;
  }
}
.a-button_red {
  background: $c--error;
  color: $c--white;
  border-radius: 4px;

  @include textPrimaryNavigation;
  font-weight: 700;

  &:hover {
    opacity: 1;
    background: $c--error-dark;
  }
}

.a-button_black {
  background: $c--black;
  color: $c--white;
}

.a-button_black--bordered {
  color: $c--black;
  border: 1px solid $c--black;
  border-radius: 4px;
}

.a-button_white,
.a-button_white--bordered {
  background: $c--white;
  color: $c--main;
  border-radius: 4px;

  &.a-button_disabled {
    background: $c--main;
    color: $c--white;
  }
}

.a-button_white--bordered {
  border: 2px solid $c--main;

  &:not(.a-button_disabled):hover {
    background: $c--main;
    color: $c--white;
  }
}

.a-button_gray {
  background: $c--gray-soft;
  color: $c--main;
  border-radius: 4px;
  border: none;
}

.a-button_gray--bordered {
  color: $c--gray;
  border: 1px solid $c--gray;
  border-radius: 4px;
}

.a-button_light-gray--bordered {
  color: $c--gray;
  border: 1px solid $c--gray;
  border-radius: 4px;
}

.a-button_green {
  color: $c--upload-button-legacy;
  border: 1px solid $c--upload-button-legacy;
  height: 30px;

  .a-button__slot-wrapper {
    font-weight: 700;

    /deep/ * {
      font-weight: 700;
    }
  }

  &:not(.a-button_disabled):hover {
    background: $c--upload-button-legacy;
    color: $c--white;
  }

  &.a-button_disabled {
    background: $c--main;
    color: $c--white;
  }
}

.a-button_ring-captcha-send-pin {
  width: 100%;
  height: 50px;
  color: #000;
  background: #fff;
  font-weight: 700;
  font-size: 20px;
  line-height: 20px;

  @include mobile {
    height: 40px;
    line-height: 40px;
  }
}

.a-button_in-progress {
  cursor: not-allowed;

  .a-button__slot-wrapper {
    opacity: 0.4;
  }
}

.input-group__error {
  position: absolute;
  top: 100%;
  left: 5px;
  color: #000;
}
</style>
